import { type ReactNode, type Ref, useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';
import type { ButtonState } from '@interxfi/design-system/components/button/shared';
import { Drawer, type DrawerProps } from '@interxfi/design-system/components/dialog/drawer/drawer';
import { useMergedDialogOpenProps } from '@interxfi/design-system/components/dialog/utils';
import ArrowLeftIcon from '@interxfi/design-system/icons/react/icon-arrow-left';
import { getNearestScrollChild } from '@interxfi/design-system/util/scroll';
import {
  FormWizardContextProvider,
  useFormWizardContext,
  useFormWizardContextValue,
  useFormWizardReset,
  useFormWizardSteps,
  useSetFormWizardContext,
} from './form-wizard-context';
import { type FormWizardFormStep, FormWizardFormStepComponent } from './step/form-wizard-form-step';
import { type FormWizardReviewStep, FormWizardReviewStepComponent } from './step/form-wizard-review-step';
import type { BaseFormWizardStepGeneric } from './step/form-wizard-step';
import { type StepArray, useFormWizardStepBuilder } from './step/form-wizard-step-builder';
import { type FormWizardSuccessStep, FormWizardSuccessStepComponent } from './step/form-wizard-success-step';

export type FormWizardStep<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}> =
  | FormWizardFormStep<TSteps, keyof TSteps, TContext, Resolver<TSteps[keyof TSteps], TContext, TSteps[keyof TSteps]>>
  | FormWizardReviewStep<TMappedData, TContext, TSteps>
  | FormWizardSuccessStep;

export type FormWizardDrawerProps = Omit<DrawerProps, 'description' | 'title' | 'onSubmit' | 'onReset'>;

interface BaseFormWizardProps<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}>
  extends Omit<DrawerProps, 'onSubmit' | 'children' | 'title'> {
  disabled?: boolean;
  handleMapFormData: (data: TSteps, ctx: TContext | undefined) => TMappedData;
  onComplete: (data: TMappedData, ctx: TContext | undefined) => void | Promise<void>;
  onReset?: VoidFunction | (() => Promise<void>);
  requireDirty?: boolean;
  title: ReactNode | ((data: TSteps) => ReactNode);
  saveCtaRef?: Ref<HTMLButtonElement>;
  saveCtaState?: ButtonState;
}

export function defaultValueMapper<T>(x: T) {
  return x;
}

interface InnerFormWizardProps<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}>
  extends BaseFormWizardProps<TSteps, TMappedData, TContext> {
  initialContext?: TContext;
  steps: FormWizardStep<TSteps, TMappedData, TContext>[];
}

function FormWizardInner<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}>({
  disabled,
  handleMapFormData,
  initialContext,
  isOpen: providedIsOpen,
  onOpenChange: providedOnOpenChange,
  onComplete,
  onReset,
  requireDirty = false,
  saveCtaRef,
  saveCtaState,
  steps,
  title,
  ...drawerProps
}: InnerFormWizardProps<TSteps, TMappedData, TContext>) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = steps[currentStepIndex];
  const clearFormWizardState = useFormWizardContext<TSteps, TContext, () => void>(useShallow((state) => state.clear));
  const context = useFormWizardContextValue<TSteps, TContext>();
  const resetWizardState = useFormWizardReset<TSteps, TContext>();
  const setContext = useSetFormWizardContext<TSteps, TContext>();
  const canGoBack = currentStepIndex > 0 && currentStep.kind !== 'success';
  const stepProgressMax = steps.reduce((accum, step) => accum + (step.kind === 'success' ? 0 : 1), 0);
  const nextStep = steps[currentStepIndex + 1];
  const stepData = useFormWizardSteps<TSteps, TContext>();

  const updateContext = useEffectEvent((ctx: TContext | undefined) => {
    setContext(ctx);
  });

  useEffect(() => {
    updateContext(initialContext);
  }, [initialContext]);

  const goNext = useCallback(() => {
    setCurrentStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [steps.length]);

  const goBackReducer = currentStep.kind === 'form' && currentStep.onGoBackReducer ? currentStep.onGoBackReducer : undefined;

  const goBack = useCallback(() => {
    if (goBackReducer) {
      const output = goBackReducer(stepData, context);
      resetWizardState(output.steps, output.context);
    }

    setCurrentStepIndex((i) => Math.max(i - 1, 0));
  }, [goBackReducer, resetWizardState, stepData, context]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setContext(initialContext);

      if (!newOpen) {
        clearFormWizardState();
        onReset?.();
        setCurrentStepIndex(0);
      }

      onOpenChange(newOpen);
    },
    [onOpenChange, onReset, clearFormWizardState, setContext, initialContext],
  );

  const hasNextStep = Boolean(nextStep);
  const willSubmitOnNext = !hasNextStep || nextStep.kind === 'success';

  const handleFormCompletion = useCallback(
    async (data: TMappedData) => {
      if (willSubmitOnNext) {
        try {
          await onComplete(data, context);

          if (hasNextStep) {
            goNext();
          } else {
            handleOpenChange(false);
          }
        } catch {
          goBack();
        }
      }
    },
    [goBack, goNext, handleOpenChange, hasNextStep, willSubmitOnNext, onComplete, context],
  );

  const handleMapAndSubmitForm = useCallback(
    async (data: TSteps) => {
      if (willSubmitOnNext) {
        await handleFormCompletion(handleMapFormData(data, context));
        return;
      }

      goNext();
    },
    [handleMapFormData, handleFormCompletion, willSubmitOnNext, goNext, context],
  );

  useLayoutEffect(() => {
    if (currentStep) {
      getNearestScrollChild(drawerRef.current)?.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentStep]);

  let children: ReactNode;

  switch (currentStep.kind) {
    case 'form': {
      children = (
        <FormWizardFormStepComponent<TSteps, typeof currentStep.id, TContext, typeof currentStep.schema>
          {...currentStep}
          context={context}
          disabled={disabled}
          key={currentStep.id.toString()}
          onSubmit={handleMapAndSubmitForm}
          requireDirty={currentStep.requireDirty ?? requireDirty}
          saveCtaRef={saveCtaRef}
          saveCtaState={saveCtaState}
        />
      );

      break;
    }
    case 'review': {
      children = (
        <FormWizardReviewStepComponent<TSteps, TMappedData, TContext>
          {...currentStep}
          goBack={goBack}
          handleMapFormData={handleMapFormData}
          onConfirm={handleFormCompletion}
          saveCtaRef={saveCtaRef}
          saveCtaState={saveCtaState}
        />
      );
      break;
    }
    case 'success': {
      children = <FormWizardSuccessStepComponent {...currentStep} />;
      break;
    }
    default:
      children = null;
  }

  const computedTitle = typeof title === 'function' ? title(stepData) : title;

  return (
    <Drawer
      closeIcon={canGoBack ? <ArrowLeftIcon aria-hidden /> : undefined}
      closeIconAction={canGoBack ? goBack : undefined}
      gap={7}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      // The progress bar doesn't show on the success step
      progress={currentStep.kind !== 'success' ? (currentStepIndex + 1) / stepProgressMax : undefined}
      title={computedTitle}
      {...drawerProps}
      ref={drawerRef}
    >
      {children}
    </Drawer>
  );
}

interface FormWizardProps<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}>
  extends BaseFormWizardProps<TSteps, TMappedData, TContext> {
  initialContext?: TContext;
  initialSteps: StepArray<TSteps, TContext>;
  initialData: TSteps;
  reviewStep?: FormWizardReviewStep<TMappedData, TContext, TSteps>;
  successStep?: FormWizardSuccessStep;
}

export function FormWizard<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}>({
  initialContext,
  initialSteps,
  initialData,
  onComplete,
  reviewStep,
  successStep,
  ...rest
}: FormWizardProps<TSteps, TMappedData, TContext>) {
  const manager = useFormWizardStepBuilder<TSteps, TContext>(...initialSteps);
  const orderedSteps = useMemo(
    () => [...manager.steps, ...(reviewStep ? [reviewStep] : []), ...(successStep ? [successStep] : [])],
    [manager.steps, reviewStep, successStep],
  );

  return (
    <FormWizardContextProvider<TSteps, TContext> initialContext={initialContext} initialData={initialData} manager={manager}>
      <FormWizardInner<TSteps, TMappedData, TContext> initialContext={initialContext} onComplete={onComplete} steps={orderedSteps} {...rest} />
    </FormWizardContextProvider>
  );
}
